import { useState, type FormEvent } from 'react';
import { useSearchParams } from 'react-router';
import { useNurseryIntake } from '../nursery/NurseryIntake';
import { QrCode } from '../nursery/QrCode';
import { api, errorMessage } from '../../shared/api/client';
import type { Family, FamilyInvitation } from '../../shared/api/types';
import { Alert, Button, Card, EmptyState, ErrorState, Field, Loading } from '../../shared/ui/components';
import { useAsync } from '../../shared/ui/useAsync';
import { useSession } from '../session/SessionProvider';
import { FamilyTreeView, type TreeFamily } from '../members/FamilyTree';

const RELATIONSHIP_LABELS: Record<string, string> = {
  head: 'Cabeza de familia',
  spouse: 'Cónyuge',
  child: 'Hijo/a',
  parent: 'Padre/Madre',
  sibling: 'Hermano/a',
  guardian: 'Tutor/a',
  other: 'Otro',
};
/** Parentescos de un adulto invitado a gestionar la familia. */
const ADULT_RELATIONSHIPS = ['spouse', 'parent', 'guardian', 'sibling', 'other'] as const;

/**
 * Mi familia: quien la gestiona registra y corrige a los menores (regla 22) e invita a otro adulto
 * a gestionarla. La Guardería usa estos tutores para dejar y recoger a los niños.
 */
export function MyFamilyCard({ personId, onFamilies }: { personId: string; onFamilies?: (count: number) => void }) {
  const families = useAsync(
    (signal) =>
      api.get<{ items: Family[] }>('/identity/me/families', signal).then((r) => {
        onFamilies?.(r.items.length);
        return r.items;
      }),
    [personId],
  );
  const invitations = useAsync((signal) => api.get<{ items: FamilyInvitation[] }>('/identity/me/family-invitations', signal).then((r) => r.items), [personId]);
  const devices = useAsync((signal) => api.get<{ items: ChildDeviceRequest[] }>('/identity/me/child-device-requests', signal).then((r) => r.items), [personId]);
  const requests = useAsync((signal) => api.get<{ items: FamilyRequest[] }>('/multichurch/me/family-requests', signal).then((r) => r.items), [personId]);
  const reload = () => {
    families.reload();
    invitations.reload();
    devices.reload();
    requests.reload();
  };
  const pending = requests.status === 'ready' ? requests.data : [];

  return (
    <Card title="Familia">
      {invitations.status === 'ready' ? invitations.data.map((i) => <InvitationNotice key={i.id} invitation={i} onDone={reload} />) : null}
      {devices.status === 'ready' ? devices.data.map((r) => <DeviceRequestNotice key={r.id} request={r} onDone={reload} />) : null}
      {pending.length ? <FamilyRequests requests={pending} onDone={reload} /> : null}
      {families.status === 'loading' ? <Loading /> : null}
      {families.status === 'error' ? <ErrorState error={families.error} onRetry={families.reload} /> : null}
      {families.status === 'ready' ? (
        <>
          {families.data.length > 0 ? <p className="muted small">Tu iglesia también ve esta familia, con tus hijos, en Miembros → Familias.</p> : null}
          {families.data.length === 0 && !(invitations.status === 'ready' && invitations.data.length > 0) && !pending.length ? <EmptyState title="Aún no has registrado tu familia." /> : null}
          {families.data.map((f) => (
            <FamilyBlock key={f.id} family={f} personId={personId} onChanged={reload} />
          ))}
          {families.data.length > 0 ? <MyFamilyTree personId={personId} /> : null}
          {families.data.length > 0 ? <ReportError /> : null}
          {/* Con una invitación pendiente no se forma otra: se acepta la de arriba. */}
          {families.data.length === 0 && !(invitations.status === 'ready' && invitations.data.length > 0) && !pending.length ? <CreateFamilyForm onCreated={reload} /> : null}
        </>
      ) : null}
    </Card>
  );
}

function useAction(onDone: () => void) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  async function run(action: () => Promise<unknown>): Promise<boolean> {
    setBusy(true);
    setError(null);
    try {
      await action();
      onDone();
      return true;
    } catch (e) {
      setError(errorMessage(e));
      return false;
    } finally {
      setBusy(false);
    }
  }
  return { busy, error, run };
}

/** El árbol con las familias relacionadas que registró la iglesia (solo si hay alguna). */
function MyFamilyTree({ personId }: { personId: string }) {
  const tree = useAsync((signal) => api.get<{ rootFamilyId: string | null; families: TreeFamily[] }>('/multichurch/me/family-tree', signal), [personId]);
  const [open, setOpen] = useState(false);
  if (tree.status !== 'ready' || !tree.data.rootFamilyId || tree.data.families.length < 2) return null;
  return (
    <div>
      <Button type="button" variant="ghost" onClick={() => setOpen((o) => !o)}>
        {open ? 'Ocultar árbol familiar' : 'Ver árbol familiar'}
      </Button>
      {open ? <FamilyTreeView families={tree.data.families} rootId={tree.data.rootFamilyId} /> : null}
    </div>
  );
}

interface FamilyRequest {
  id: string;
  description: string;
  createdAt: string;
}

/** Lo que pediste en Mi familia y espera a que lo apruebe la administración de tu iglesia. */
function FamilyRequests({ requests, onDone }: { requests: FamilyRequest[]; onDone: () => void }) {
  const { busy, error, run } = useAction(onDone);
  return (
    <div className="notice">
      <p>
        <strong>Esperando aprobación de tu iglesia.</strong> La administración o el pastor revisan los cambios a las familias.
      </p>
      {error ? <Alert>{error}</Alert> : null}
      <ul className="list">
        {requests.map((r) => (
          <li key={r.id} className="list__item list__item--actions">
            <span>Pediste {r.description}.</span>
            <Button type="button" variant="ghost" disabled={busy} onClick={() => void run(() => api.delete(`/multichurch/me/family-requests/${r.id}`))}>
              Cancelar
            </Button>
          </li>
        ))}
      </ul>
    </div>
  );
}

interface ChildDeviceRequest {
  id: string;
  childName: string;
  device: string | null;
  createdAt: string;
}

/** «Soy hijo o hija»: el aparato del hijo espera tu aprobación. */
function DeviceRequestNotice({ request: r, onDone }: { request: ChildDeviceRequest; onDone: () => void }) {
  const { busy, error, run } = useAction(onDone);
  const base = `/identity/me/child-device-requests/${r.id}`;
  return (
    <div className="family">
      {error ? <Alert>{error}</Alert> : null}
      <p>
        <strong>{r.childName}</strong> quiere entrar a KEORA en su propio aparato{r.device ? <span className="muted small"> ({deviceName(r.device)})</span> : null}. Si no fue tu hijo,
        recházalo.
      </p>
      <div className="actions actions--wrap">
        <Button type="button" busy={busy} onClick={() => void run(() => api.post(`${base}/approve`))}>
          Aprobar
        </Button>
        <Button type="button" variant="secondary" disabled={busy} onClick={() => void run(() => api.post(`${base}/deny`))}>
          Rechazar
        </Button>
      </div>
    </div>
  );
}

/** Nombre corto del aparato a partir del navegador. */
function deviceName(ua: string): string {
  if (/iPad/i.test(ua)) return 'iPad';
  if (/iPhone/i.test(ua)) return 'iPhone';
  if (/Android/i.test(ua)) return /Mobile/i.test(ua) ? 'celular Android' : 'tablet Android';
  if (/Windows|Macintosh|Linux/i.test(ua)) return 'computadora';
  return 'aparato';
}

/** Ligar el aparato de un hijo sin cuenta: QR de pocos minutos y los aparatos que ya tiene. */
function ChildDevices({ child, familyId, onClose }: { child: Family['members'][number]; familyId: string; onClose: () => void }) {
  const devices = useAsync((signal) => api.get<{ items: { id: string; device: string | null; since: string }[] }>(`/security/hijo/${child.personId}/aparatos`, signal).then((r) => r.items), [child.personId]);
  const [code, setCode] = useState<{ url: string; expiresAt: string } | null>(null);
  const { busy, error, run } = useAction(devices.reload);

  async function makeCode() {
    await run(async () => {
      const r = await api.post<{ code: string; expiresAt: string }>(`/identity/me/families/${familyId}/children/${child.personId}/device-code`);
      setCode({ url: `${window.location.origin}/hijo?codigo=${encodeURIComponent(r.code)}`, expiresAt: r.expiresAt });
    });
  }

  return (
    <div className="stack">
      <strong>Aparatos de {child.givenName}</strong>
      {error ? <Alert>{error}</Alert> : null}
      {code ? (
        <>
          <p className="small muted">
            Que {child.givenName} lo escanee con la cámara de su celular o tablet. Sirve una vez y vence a las{' '}
            {new Date(code.expiresAt).toLocaleTimeString('es-MX', { hour: 'numeric', minute: '2-digit' })}.
          </p>
          <QrCode value={code.url} label={`Código para ligar el aparato de ${child.givenName}`} />
          <Button type="button" variant="ghost" onClick={() => void navigator.clipboard?.writeText(code.url)}>
            Copiar enlace
          </Button>
        </>
      ) : (
        <p className="small muted">
          Entrará con lo de un hijo: Biblia, devocional y Mi familia, sin chats ni pagos. También puede pedirlo desde «Soy hijo o hija» al entrar, y te
          llega el aviso para aprobarlo.
        </p>
      )}
      <div className="actions actions--wrap">
        <Button type="button" busy={busy} onClick={() => void makeCode()}>
          {code ? 'Otro código' : 'Mostrar código QR'}
        </Button>
        <Button type="button" variant="secondary" onClick={onClose}>
          Cerrar
        </Button>
      </div>
      {devices.status === 'ready' && devices.data.length ? (
        <ul className="list">
          {devices.data.map((d) => (
            <li key={d.id} className="list__item list__item--actions">
              <span>
                {d.device ? deviceName(d.device) : 'Aparato'} <span className="muted small">· desde {new Date(d.since).toLocaleDateString('es-MX')}</span>
              </span>
              <Button type="button" variant="ghost" disabled={busy} onClick={() => void run(() => api.delete(`/security/hijo/${child.personId}/aparatos/${d.id}`))}>
                Quitar
              </Button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

/** «Informar un error»: la administración armó la familia; si algo está mal, se le avisa al pastor y a la administración. */
function ReportError() {
  const [open, setOpen] = useState(false);
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.post('/multichurch/me/family-report', { message: String(new FormData(event.currentTarget).get('message') ?? '') });
      setSent(true);
      setOpen(false);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }
  if (sent) return <Alert tone="success">Listo, le avisamos al pastor y a la administración de tu iglesia.</Alert>;
  if (!open) {
    return (
      <p className="small muted">
        ¿Algo está mal en tu familia?{' '}
        <Button type="button" variant="ghost" onClick={() => setOpen(true)}>
          Informar un error
        </Button>
      </p>
    );
  }
  return (
    <form className="form" onSubmit={onSubmit} noValidate>
      {error ? <Alert>{error}</Alert> : null}
      <div className="field">
        <label htmlFor="family-report">¿Qué está mal?</label>
        <textarea id="family-report" name="message" rows={3} maxLength={1000} required placeholder="Ej. Falta mi hijo Juan, o yo no soy de esta familia." />
      </div>
      <div className="actions">
        <Button type="submit" busy={busy}>
          Enviar
        </Button>
        <Button type="button" variant="secondary" disabled={busy} onClick={() => setOpen(false)}>
          Cancelar
        </Button>
      </div>
    </form>
  );
}

function InvitationNotice({ invitation: i, onDone }: { invitation: FamilyInvitation; onDone: () => void }) {
  const { busy, error, run } = useAction(onDone);
  const base = `/identity/me/family-invitations/${i.id}`;
  return (
    <div className="family">
      {error ? <Alert>{error}</Alert> : null}
      <p>
        {i.relationship === 'child' ? (
          <>
            <strong>{i.invitedBy}</strong> quiere unir tu cuenta a su familia <strong>{i.familyName}</strong> como hijo o hija. Si aceptas, quedas en tu familia con tu propia cuenta, también en Mi familia.
          </>
        ) : (
          <>
            <strong>{i.invitedBy}</strong> te invitó a su familia <strong>{i.familyName}</strong> como {RELATIONSHIP_LABELS[i.relationship]?.toLowerCase() ?? 'integrante'}. Si aceptas, podrán gestionar juntos a sus hijos (también en la Guardería).
          </>
        )}
      </p>
      <div className="actions actions--wrap">
        <Button type="button" busy={busy} onClick={() => void run(() => api.post(`${base}/accept`))}>
          Aceptar
        </Button>
        <Button type="button" variant="secondary" disabled={busy} onClick={() => void run(() => api.post(`${base}/decline`))}>
          No, gracias
        </Button>
      </div>
    </div>
  );
}

function FamilyBlock({ family: f, personId, onChanged }: { family: Family; personId: string; onChanged: () => void }) {
  const { busy, error, run } = useAction(onChanged);
  const [editing, setEditing] = useState<string | null>(null);
  const [linking, setLinking] = useState<string | null>(null);
  const me = f.members.find((m) => m.personId === personId);
  const manager = Boolean(me?.isManager);
  const base = `/identity/me/families/${f.id}`;

  function rename() {
    const name = window.prompt('Nuevo nombre de la familia', f.name);
    if (name === null || !name.trim()) return;
    void run(() => api.patch(base, { name: name.trim() }));
  }

  function linkAccount(m: Family['members'][number]) {
    const email = window.prompt(`Correo con el que ${m.givenName} entra a KEORA. Le llegará un aviso para aceptar.`);
    if (email === null || !email.trim()) return;
    void run(() => api.post(`${base}/children/${m.personId}/account`, { email: email.trim() }));
  }

  function remove(m: Family['members'][number]) {
    const self = m.personId === personId;
    const question = self ? `¿Salir de "${f.name}"?` : `¿Quitar a ${`${m.givenName} ${m.familyName}`.trim()} de "${f.name}"?`;
    if (!window.confirm(question)) return;
    void run(() => api.delete(`${base}/members/${m.personId}`));
  }

  return (
    <div className="family">
      <h3>{f.name}</h3>
      {error ? <Alert>{error}</Alert> : null}
      <ul className="list">
        {f.members.map((m) =>
          editing === m.personId ? (
            <li key={m.personId} className="list__item">
              <ChildForm familyId={f.id} child={m} onDone={() => { setEditing(null); onChanged(); }} onCancel={() => setEditing(null)} />
            </li>
          ) : linking === m.personId ? (
            <li key={m.personId} className="list__item">
              <ChildDevices child={m} familyId={f.id} onClose={() => setLinking(null)} />
            </li>
          ) : (
            <li key={m.personId} className="list__item list__item--actions">
              <span>
                {`${m.givenName} ${m.familyName}`.trim()} <span className="muted">· {RELATIONSHIP_LABELS[m.relationship] ?? m.relationship}</span>
              </span>
              <span className="actions">
                {manager && m.relationship === 'child' && !m.hasAccount ? (
                  <Button type="button" variant="ghost" disabled={busy} onClick={() => setEditing(m.personId)}>
                    Corregir
                  </Button>
                ) : null}
                {manager && m.relationship === 'child' && !m.hasAccount ? (
                  <Button type="button" variant="ghost" disabled={busy} onClick={() => setLinking(m.personId)}>
                    Ligar su celular
                  </Button>
                ) : null}
                {manager && m.relationship === 'child' && !m.hasAccount ? (
                  <Button type="button" variant="ghost" disabled={busy} onClick={() => linkAccount(m)}>
                    Unir su cuenta
                  </Button>
                ) : null}
                {manager || m.personId === personId ? (
                  <Button type="button" variant="ghost" disabled={busy} onClick={() => remove(m)}>
                    {m.personId === personId ? 'Salir' : 'Quitar'}
                  </Button>
                ) : null}
              </span>
            </li>
          ),
        )}
        {f.pendingInvitations.map((i) => (
          <li key={i.id} className="list__item list__item--actions">
            <span>
              {i.name} <span className="muted">· {RELATIONSHIP_LABELS[i.relationship] ?? i.relationship} · invitación pendiente</span>
            </span>
            <Button type="button" variant="ghost" disabled={busy} onClick={() => void run(() => api.delete(`${base}/invitations/${i.id}`))}>
              Cancelar
            </Button>
          </li>
        ))}
      </ul>
      {manager ? (
        <div className="actions actions--wrap">
          <AddChildForm familyId={f.id} familyName={me?.familyName ?? ''} onAdded={onChanged} />
          <InviteForm familyId={f.id} onSent={onChanged} />
          <Button type="button" variant="ghost" disabled={busy} onClick={rename}>
            Cambiar nombre
          </Button>
        </div>
      ) : null}
    </div>
  );
}

/** Invitar a otro adulto con cuenta (la mamá, el papá, un tutor) a gestionar la familia. */
function InviteForm({ familyId, onSent }: { familyId: string; onSent: () => void }) {
  const [open, setOpen] = useState(false);
  const { busy, error, run } = useAction(onSent);

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    if (await run(() => api.post(`/identity/me/families/${familyId}/invitations`, { email: String(form.get('email')), relationship: String(form.get('relationship')) }))) setOpen(false);
  }

  if (!open) {
    return (
      <Button type="button" variant="ghost" onClick={() => setOpen(true)}>
        Invitar a otro papá, mamá o tutor
      </Button>
    );
  }
  return (
    <form className="form" onSubmit={onSubmit}>
      {error ? <Alert>{error}</Alert> : null}
      <p className="muted small">Debe tener cuenta en KEORA. Le llegará un aviso para aceptar.</p>
      <Field label="Su correo" name="email" type="email" required maxLength={254} />
      <div className="field">
        <label htmlFor={`invite-rel-${familyId}`}>Parentesco</label>
        <select id={`invite-rel-${familyId}`} name="relationship" defaultValue="spouse">
          {ADULT_RELATIONSHIPS.map((r) => (
            <option key={r} value={r}>
              {RELATIONSHIP_LABELS[r]}
            </option>
          ))}
        </select>
      </div>
      <div className="actions actions--wrap">
        <Button type="submit" busy={busy}>
          Invitar
        </Button>
        <Button type="button" variant="secondary" onClick={() => setOpen(false)}>
          Cancelar
        </Button>
      </div>
    </form>
  );
}

function GenderSelect({ id, value }: { id: string; value?: string | null }) {
  return (
    <div className="field">
      <label htmlFor={id}>Es</label>
      <select id={id} name="gender" defaultValue={value ?? ''}>
        <option value="">Sin indicar</option>
        <option value="femenino">Niña</option>
        <option value="masculino">Niño</option>
      </select>
    </div>
  );
}

const childBody = (form: FormData) => ({
  givenName: String(form.get('givenName')),
  familyName: String(form.get('familyName')),
  birthDate: String(form.get('birthDate')),
  gender: String(form.get('gender') ?? '') || null,
});

/** Corregir los datos de un menor ya registrado. */
function ChildForm({ familyId, child, onDone, onCancel }: { familyId: string; child: Family['members'][number]; onDone: () => void; onCancel: () => void }) {
  const { busy, error, run } = useAction(onDone);

  function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    void run(() => api.put(`/identity/me/families/${familyId}/children/${child.personId}`, childBody(form)));
  }

  return (
    <form className="form" onSubmit={onSubmit}>
      {error ? <Alert>{error}</Alert> : null}
      <div className="form__row">
        <Field label="Nombre" name="givenName" required maxLength={100} defaultValue={child.givenName} />
        <Field label="Apellidos" name="familyName" maxLength={100} defaultValue={child.familyName} />
      </div>
      <Field label="Fecha de nacimiento" name="birthDate" type="date" required defaultValue={child.birthDate ?? ''} />
      <GenderSelect id={`child-gender-${child.personId}`} value={child.gender} />
      <div className="actions actions--wrap">
        <Button type="submit" busy={busy}>
          Guardar
        </Button>
        <Button type="button" variant="secondary" onClick={onCancel}>
          Cancelar
        </Button>
      </div>
    </form>
  );
}

/** Un menor es una Persona global sin cuenta: lo registra quien gestiona la familia (regla 22). */
function AddChildForm({ familyId, familyName, onAdded }: { familyId: string; familyName: string; onAdded: () => void }) {
  const { state } = useSession();
  const inChurch = state.status === 'authenticated' && !!state.me.activeChurchId;
  // Mi hogar manda aquí a quien usará la guardería: llega con el nombre y la fecha (?hijo=…&nacimiento=…).
  const [params] = useSearchParams();
  const fromHome = params.get('hijo');
  const [open, setOpen] = useState(Boolean(fromHome));
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [birthDate, setBirthDate] = useState(fromHome ? (params.get('nacimiento') ?? '') : '');
  const nursery = useNurseryIntake(`new-child-${familyId}`, birthDate || null, inChurch);

  function close() {
    setOpen(false);
    setBirthDate('');
    nursery.reset();
    setError(null);
  }

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const missing = nursery.missing();
    if (missing) {
      setError(missing);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.post(`/identity/me/families/${familyId}/children`, { ...childBody(form), nursery: await nursery.read(form) });
      close();
      onAdded();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  if (!open) {
    return (
      <Button type="button" variant="ghost" onClick={() => setOpen(true)}>
        Agregar hijo o hija
      </Button>
    );
  }
  return (
    <form className="form" onSubmit={onSubmit}>
      {error ? <Alert>{error}</Alert> : null}
      <div className="form__row">
        <Field label="Nombre" name="givenName" required maxLength={100} defaultValue={fromHome ?? ''} autoFocus={Boolean(fromHome)} />
        <Field label="Apellidos" name="familyName" maxLength={100} defaultValue={familyName} />
      </div>
      <Field label="Fecha de nacimiento" name="birthDate" type="date" required value={birthDate} onChange={(e) => setBirthDate(e.target.value)} />
      <GenderSelect id={`new-child-gender-${familyId}`} />
      {nursery.fields}
      <div className="actions actions--wrap">
        <Button type="submit" busy={busy}>
          Agregar
        </Button>
        <Button type="button" variant="secondary" onClick={close}>
          Cancelar
        </Button>
      </div>
    </form>
  );
}

function CreateFamilyForm({ onCreated }: { onCreated: () => void }) {
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const formEl = event.currentTarget;
    const name = String(new FormData(formEl).get('name') ?? '').trim();
    if (!name) {
      setError('Escribe el nombre de la familia.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.post('/identity/me/families', { name });
      formEl.reset();
      onCreated();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="form form--inline" onSubmit={onSubmit} noValidate>
      {error ? <Alert>{error}</Alert> : null}
      <Field label="Nombre de la familia" name="name" required maxLength={120} placeholder="Familia Pérez" />
      <Button type="submit" variant="secondary" busy={busy}>
        Crear familia
      </Button>
    </form>
  );
}
